import { useEffect, useState } from 'react';
import { AppTopbar, DesktopSidebar, MobileNavigation, SearchPopover, type WorkspaceAction } from './components/AppChrome';
import { fetchChangeRequests, fetchGraph, getApiBase, openAsset, type GenealogyGraph } from './api';
import { events as fallbackEvents, media as fallbackMedia, people as fallbackPeople } from './data';
import { MemberWorkspace, PersonEditor } from './features/members/MemberWorkspace';
import { DataTools, LoginModal, MediaLibrary, ReviewQueue, SettingsPanel } from './features/overlays/AppOverlays';
import { PersonDetails } from './features/person/PersonDetails';
import { FamilyTreeCanvas } from './features/tree/FamilyTreeCanvas';
import type { AuthSession, ChangeRequest, Person } from './types';

type Overlay = 'members' | 'media' | 'data' | 'settings' | 'login' | 'review' | null;

function readSession(): AuthSession {
  try {
    return JSON.parse(localStorage.getItem('yunpu_session') || '') as AuthSession;
  } catch {
    return { role: 'guest', name: '访客' };
  }
}

export default function App() {
  const [graph, setGraph] = useState<GenealogyGraph>({ people: fallbackPeople, media: fallbackMedia, events: fallbackEvents, online: false });
  const [session, setSession] = useState<AuthSession>(readSession);
  const [selectedId, setSelectedId] = useState(() => new URLSearchParams(window.location.search).get('person') ?? 'p11');
  const [query, setQuery] = useState('');
  const [overlay, setOverlay] = useState<Overlay>(null);
  const [editorPerson, setEditorPerson] = useState<Person | null | undefined>(undefined);
  const [pending, setPending] = useState<ChangeRequest[]>([]);
  const [notice, setNotice] = useState('');
  const [mobileDetailsOpen, setMobileDetailsOpen] = useState(false);
  const [activeAction, setActiveAction] = useState<WorkspaceAction>('tree');
  const [apiBase, setApiBaseState] = useState(getApiBase());

  const selected = graph.people.find((person) => person.id === selectedId) ?? graph.people[0] ?? fallbackPeople[0];

  const load = async (nextSession = session) => {
    const nextGraph = await fetchGraph(nextSession);
    setGraph(nextGraph);
    if (!nextGraph.people.some((person) => person.id === selectedId)) setSelectedId(nextGraph.people[0]?.id ?? 'p11');
    if (nextSession.role === 'admin') {
      try { setPending(await fetchChangeRequests(nextSession)); }
      catch { setPending([]); }
    } else setPending([]);
  };

  useEffect(() => { void load(session); }, [session.role, session.token]);
  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(''), 2800);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const refresh = async (message: string) => {
    setNotice(message);
    await load(session);
  };

  const requireLogin = (action: () => void) => {
    if (session.role === 'guest') setOverlay('login');
    else action();
  };

  const handleWorkspaceAction = (action: WorkspaceAction) => {
    setActiveAction(action);
    if (action === 'tree') return;
    if (action === 'members') setOverlay('members');
    if (action === 'media') setOverlay('media');
    if (action === 'data') requireLogin(() => setOverlay('data'));
    if (action === 'settings') setOverlay('settings');
  };

  const selectPerson = (person: Person) => {
    setSelectedId(person.id);
    setMobileDetailsOpen(true);
    setQuery('');
  };

  return (
    <main className="app-shell">
      <div className="cultural-backdrop" aria-hidden="true"><span /><i /><b /></div>
      <DesktopSidebar active={activeAction} onAction={handleWorkspaceAction} />

      <section className="workspace-shell">
        <AppTopbar
          query={query}
          onQuery={setQuery}
          online={graph.online}
          session={session}
          pendingCount={pending.length}
          onAdd={() => requireLogin(() => setEditorPerson(null))}
          onReview={() => setOverlay('review')}
          onLogin={() => setOverlay('login')}
          onLogout={() => { localStorage.removeItem('yunpu_session'); setSession({ role: 'guest', name: '访客' }); setNotice('已退出登录'); }}
          onOpenMenu={() => setOverlay('members')}
        />
        <SearchPopover query={query} people={graph.people} onSelect={(id) => { const person = graph.people.find((item) => item.id === id); if (person) selectPerson(person); }} />

        <div className="workspace-content">
          <FamilyTreeCanvas people={graph.people} selected={selected} onSelect={selectPerson} />
          <PersonDetails
            person={selected}
            people={graph.people}
            media={graph.media}
            events={graph.events}
            role={session.role}
            mobileOpen={mobileDetailsOpen}
            onCloseMobile={() => setMobileDetailsOpen(false)}
            onEdit={() => requireLogin(() => setEditorPerson(selected))}
            onUpload={() => requireLogin(() => setOverlay('media'))}
            onOpenMedia={(item) => { void openAsset(session, item.url).catch((reason) => setNotice(reason instanceof Error ? reason.message : '无法打开资料')); }}
            onShare={async () => { const url = new URL(window.location.href); url.searchParams.set('person', selected.id); await navigator.clipboard.writeText(url.toString()); setNotice('谱系链接已复制'); }}
          />
        </div>
      </section>

      <MobileNavigation active={activeAction} session={session} onAction={handleWorkspaceAction} onLogin={() => setOverlay('login')} />
      {notice && <div className="toast-message">{notice}</div>}

      {overlay === 'login' && <LoginModal onClose={() => setOverlay(null)} onLoggedIn={(nextSession) => { localStorage.setItem('yunpu_session', JSON.stringify(nextSession)); setSession(nextSession); setNotice(`已登录为${nextSession.role === 'admin' ? '管理员' : '家族成员'}`); }} />}
      {overlay === 'members' && <MemberWorkspace session={session} people={graph.people} onClose={() => { setOverlay(null); setActiveAction('tree'); }} onChanged={refresh} onSelect={selectPerson} />}
      {overlay === 'media' && <MediaLibrary session={session} people={graph.people} media={graph.media} selected={selected} onClose={() => { setOverlay(null); setActiveAction('tree'); }} onChanged={refresh} />}
      {overlay === 'data' && <DataTools session={session} onClose={() => { setOverlay(null); setActiveAction('tree'); }} onChanged={refresh} />}
      {overlay === 'review' && <ReviewQueue session={session} requests={pending} onClose={() => setOverlay(null)} onChanged={refresh} />}
      {overlay === 'settings' && <SettingsPanel apiBase={apiBase} onClose={() => { setOverlay(null); setActiveAction('tree'); }} onSaved={(value) => { setApiBaseState(value); setOverlay(null); void refresh('API 地址已更新'); }} />}
      {editorPerson !== undefined && <PersonEditor session={session} people={graph.people} person={editorPerson ?? undefined} onClose={() => setEditorPerson(undefined)} onSaved={refresh} />}
    </main>
  );
}
